/*
 * Copyright 2022-2026 NXGN Management, LLC. All Rights Reserved.
 */

import { Injectable } from "@angular/core";
import { CreditCardEnum, Merchant } from "../models";
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { validateAmexFee } from "../validators/MPCustomValidator";
import { CREDIT_CARD_CONSTANTS, PATTERN_CONSTANTS } from "../utils/data.rest.constants";
import { MerchantService } from "./merchant.service";
import * as moment from "moment";
import { AuthService } from "./auth.service";

@Injectable({
  providedIn: "root"
})
export class SharedFormService {
  supportedCards = CREDIT_CARD_CONSTANTS.CREDIT_CARDS;

  constructor(private fb: FormBuilder, private merchantService: MerchantService, private authService: AuthService) {}

  getMerchantAddressFormGroup(merchant: Merchant): FormGroup {
    const group: FormGroup = this.fb.group({
      address1: [merchant.merchantAddress?.address1, [Validators.maxLength(99)]],
      address2: [merchant.merchantAddress?.address2, [Validators.maxLength(99)]],
      city: [merchant.merchantAddress?.city, [Validators.maxLength(49)]],
      state: [merchant.merchantAddress?.state],
      zip: [merchant.merchantAddress?.zip, [Validators.pattern(PATTERN_CONSTANTS.NUMBER), Validators.maxLength(11)]],
      country: [merchant.merchantAddress?.country]
    });
    if (this.merchantService.isWorldPay(merchant)) {
      group.controls.address1.addValidators(Validators.required);
      group.controls.city.addValidators(Validators.required);
      group.controls.state.addValidators(Validators.required);
      group.controls.zip.addValidators(Validators.required);
      group.controls.country.addValidators(Validators.required);
    }
    return group;
  }

  getRemitAddressFormGroup(merchant: Merchant): FormGroup {
    return this.fb.group({
      merchantName: [merchant.remitToAddress?.merchantName, [Validators.maxLength(99)]],
      address1: [merchant.remitToAddress?.address1, [Validators.required, Validators.maxLength(99)]],
      address2: [merchant.remitToAddress?.address2, [Validators.maxLength(99)]],
      city: [merchant.remitToAddress?.city, [Validators.required, Validators.maxLength(49)]],
      state: [merchant.remitToAddress?.state, [Validators.required]],
      zip: [
        merchant.remitToAddress?.zip,
        [Validators.required, Validators.pattern(PATTERN_CONSTANTS.NUMBER), Validators.maxLength(11)]
      ],
      country: [merchant.remitToAddress?.country, Validators.required]
    });
  }

  getContactsForm(merchant: Merchant): FormGroup {
    const group: FormGroup = this.fb.group({
      primaryContactFirstName: [merchant.primaryContactFirstName],
      primaryContactLastName: [merchant.primaryContactLastName],
      primaryContactEmail: [merchant.primaryContactEmail, [Validators.email]],
      primaryContactPhoneNumber: [merchant.primaryContactPhoneNumber, [Validators.pattern(PATTERN_CONSTANTS.NUMBER_10)]]
    });
    if (this.merchantService.isWorldPay(merchant)) {
      group.controls.primaryContactFirstName.addValidators(Validators.required);
      group.controls.primaryContactLastName.addValidators(Validators.required);
      group.controls.primaryContactEmail.addValidators(Validators.required);
      group.controls.primaryContactPhoneNumber.addValidators([
        Validators.pattern(PATTERN_CONSTANTS.NUMBER_10),
        Validators.required
      ]);
    }
    return group;
  }

  getLegalEntity(merchant: Merchant): FormGroup {
    const group: FormGroup = this.fb.group({
      merchantName: this.fb.control(merchant?.merchantLegalName, [Validators.maxLength(99)]),
      phoneNumber: this.fb.control(merchant?.legalEntityPhoneNumber, Validators.pattern(PATTERN_CONSTANTS.NUMBER_10)),
      legalAddressLine1: this.fb.control(merchant.legalEntityAddress?.address1),
      legalAddressLine2: this.fb.control(merchant.legalEntityAddress?.address2),
      legalCity: this.fb.control(merchant.legalEntityAddress?.city),
      legalState: this.fb.control(merchant.legalEntityAddress?.state),
      legalZip: this.fb.control(merchant.legalEntityAddress?.zip, [
        Validators.pattern(PATTERN_CONSTANTS.NUMBER),
        Validators.maxLength(11)
      ]),
      legalCountry: this.fb.control(merchant.legalEntityAddress?.country)
    });
    if (this.merchantService.isWorldPay(merchant)) {
      group.controls.merchantName.addValidators(Validators.required);
    }
    return group;
  }

  addCheckboxes(acceptedCardsFromGroup: FormGroup, merchant: Merchant) {
    Object.keys(this.supportedCards).forEach((key) =>
      acceptedCardsFromGroup.addControl(
        key,
        new FormControl({
          value: Object.values(merchant.acceptedCreditCards).includes(<CreditCardEnum>key),
          disabled: this.merchantService.isWorldPay(merchant) && (key === "VISA" || key === "MCARD")
        })
      )
    );
    if (this.merchantService.isWorldPay(merchant)) {
      acceptedCardsFromGroup.get("AMEX")?.setValidators(validateAmexFee(merchant));
    }
  }

  updateMerchantGeneralInfo(merchant: Merchant, generalInfoFormGroup: FormGroup): void {
    merchant.merchantName = generalInfoFormGroup.getRawValue().merchantName;
    merchant.doingBusinessAs = generalInfoFormGroup.getRawValue().doingBusinessAs;
    merchant.alsoKnownAsName = generalInfoFormGroup.getRawValue().alsoKnownAsName;
    merchant.externalMerchantId = generalInfoFormGroup.getRawValue().externalMerchantId;
    merchant.customerAccountNumber = generalInfoFormGroup.getRawValue().customerAccountNumber;
    merchant.phoneNumber = generalInfoFormGroup.getRawValue().phoneNumber;
    merchant.maxTransactionLimit = generalInfoFormGroup.getRawValue().maxTransactionLimit * 100;
    if (this.merchantService.isWorldPay(merchant)) {
      merchant.accountDetails.worldPayAccountDetails.billingDescriptor =
        generalInfoFormGroup.getRawValue().billingDescriptor;
      merchant.accountDetails.worldPayAccountDetails.ownershipType = generalInfoFormGroup.getRawValue().ownershipType;
      merchant.accountDetails.worldPayAccountDetails.businessType = generalInfoFormGroup.getRawValue().businessType;
      merchant.accountDetails.worldPayAccountDetails.businessEstablishedDate = this.merchantService.dateToMMddyyyy(
        generalInfoFormGroup.getRawValue().businessEstablishedDate
      );
      merchant.accountDetails.worldPayAccountDetails.websiteURL = generalInfoFormGroup.getRawValue().websiteURL;
      merchant.accountDetails.worldPayAccountDetails.mccCode = generalInfoFormGroup.getRawValue().mccCode;
      merchant.payAPICustomer = generalInfoFormGroup.getRawValue().payApiCustomers;
    } else if (this.merchantService.isInstaMed(merchant)) {
      merchant.suppressZeroBalancePayment = generalInfoFormGroup.getRawValue().suppressZeroDollarPayment;
      merchant.achEcheckEnabled = generalInfoFormGroup.getRawValue().achEcheckEnabled;
      merchant.amexEnabled = generalInfoFormGroup.getRawValue().amexEnabled;
      if (merchant.migratedToInstaMed) {
        if (merchant.merchantMigrationDetails && generalInfoFormGroup.getRawValue().instaMedConversionStatus) {
          if (!this.authService.hasProvisionRole()) {
            merchant.merchantMigrationDetails.effectiveDate = !generalInfoFormGroup.getRawValue().tokenConversionDate
              ? ""
              : moment(generalInfoFormGroup.getRawValue()?.tokenConversionDate).format("YYYY-MM-DD");
          }
          merchant.merchantMigrationDetails.previousMmid = Number(generalInfoFormGroup.getRawValue().previousMMID);
          merchant.merchantMigrationDetails.migratedMerchantName = generalInfoFormGroup.getRawValue().merchantName;
          merchant.merchantMigrationDetails.toBeMigrated = generalInfoFormGroup.getRawValue().instaMedConversionStatus;
        } else {
          merchant.merchantMigrationDetails = {
            effectiveDate: "",
            instaMedMmid: 0,
            migratedMerchantName: "",
            migrationStatus: "",
            previousMerchantName: "",
            previousMmid: 0,
            previousProcessor: "",
            toBeMigrated: false,
            practiceId: 0
          };
        }
      } else {
        merchant.merchantMigrationDetails = null;
      }
      merchant.ngepmStatement = generalInfoFormGroup.getRawValue().ngepmStatement;
      merchant.balanceIncludesBadDebt = generalInfoFormGroup.getRawValue().balanceIncludesBadDebt;
      merchant.directBatching = generalInfoFormGroup.getRawValue().directBatching;
      merchant.siteId = generalInfoFormGroup.getRawValue().siteId;
      merchant.practiceId = generalInfoFormGroup.getRawValue().enterprisePracticeId;
      merchant.enterpriseId = generalInfoFormGroup.getRawValue().enterpriseId;
      merchant.clientName = generalInfoFormGroup.getRawValue().clientName;
      merchant.practiceName = generalInfoFormGroup.getRawValue().practiceNameSearch;
      merchant.gcn = generalInfoFormGroup.getRawValue().gcn;
      merchant.locationId = generalInfoFormGroup.getRawValue().locationId;
      merchant.locationName = generalInfoFormGroup.getRawValue().locationName;
      merchant.locationAlias = generalInfoFormGroup.getRawValue().locationAlias;
    }
  }

  updateCustomerContact(merchant: Merchant, contactsFormGroup: FormGroup): void {
    merchant.primaryContactFirstName = contactsFormGroup.getRawValue().primaryContactFirstName;
    merchant.primaryContactLastName = contactsFormGroup.getRawValue().primaryContactLastName;
    merchant.primaryContactEmail = contactsFormGroup.getRawValue().primaryContactEmail;
    merchant.primaryContactPhoneNumber = contactsFormGroup.getRawValue().primaryContactPhoneNumber;
  }

  updateMerchantAddress(
    merchant: Merchant,
    merchantAddressFormGroup: FormGroup,
    remitAddressFormGroup?: FormGroup
  ): void {
    merchant.merchantAddress = {
      address1: merchantAddressFormGroup.getRawValue().address1,
      address2: merchantAddressFormGroup.getRawValue().address2,
      city: merchantAddressFormGroup.getRawValue().city,
      state: merchantAddressFormGroup.getRawValue().state,
      zip: merchantAddressFormGroup.getRawValue().zip,
      country: merchantAddressFormGroup.getRawValue().country
    };
    if (remitAddressFormGroup) {
      merchant.remitToAddress = {
        merchantName: remitAddressFormGroup.getRawValue().merchantName,
        address1: remitAddressFormGroup.getRawValue().address1,
        address2: remitAddressFormGroup.getRawValue().address2,
        city: remitAddressFormGroup.getRawValue().city,
        state: remitAddressFormGroup.getRawValue().state,
        zip: remitAddressFormGroup.getRawValue().zip,
        country: remitAddressFormGroup.getRawValue().country
      };
    } else {
      merchant.remitToAddress = undefined;
    }
  }

  updateLegalEntity(merchant: Merchant, legalEntityFormGroup: FormGroup): void {
    merchant.merchantLegalName = legalEntityFormGroup.getRawValue().merchantName;
    merchant.legalEntityPhoneNumber = legalEntityFormGroup.getRawValue().phoneNumber;
    merchant.legalEntityAddress.address1 = legalEntityFormGroup.getRawValue().legalAddressLine1;
    merchant.legalEntityAddress.address2 = legalEntityFormGroup.getRawValue().legalAddressLine2;
    merchant.legalEntityAddress.city = legalEntityFormGroup.getRawValue().legalCity;
    merchant.legalEntityAddress.state = legalEntityFormGroup.getRawValue().legalState;
    merchant.legalEntityAddress.zip = legalEntityFormGroup.getRawValue().legalZip;
    merchant.legalEntityAddress.country = legalEntityFormGroup.getRawValue().legalCountry;
  }

  updatedAcceptedCreditCards(merchant: Merchant, acceptedCardsFormGroup: FormGroup): void {
    const updatedCreditCardList: CreditCardEnum[] = [];
    if (!acceptedCardsFormGroup || !acceptedCardsFormGroup?.controls) {
      merchant.acceptedCreditCards = [];
      return;
    }
    Object.keys(acceptedCardsFormGroup?.controls).forEach((key) => {
      if (acceptedCardsFormGroup.get(key)?.value) {
        updatedCreditCardList.push(<CreditCardEnum>key);
      }
    });
    merchant.acceptedCreditCards = updatedCreditCardList;
  }

  updateBeneficialOwner(merchant: Merchant, ownerFrom: FormArray) {
    merchant.accountDetails.worldPayAccountDetails.beneficialOwners = ownerFrom.getRawValue();
    merchant.accountDetails.worldPayAccountDetails.beneficialOwners.forEach((_, index: number) => {
      merchant.accountDetails.worldPayAccountDetails.beneficialOwners[index].ownerType = ownerFrom.controls[index]
        .get("ownerType")
        ?.value.replace(/\s/g, "");
      merchant.accountDetails.worldPayAccountDetails.beneficialOwners[index].dateOfBirth =
        this.merchantService.dateToMMddyyyy(ownerFrom.controls[index].get("dateOfBirth")?.value);
    });
  }
}
